import React, { useCallback, useMemo, useState } from 'react';
import PageHeader from 'components/PageHeader';
import type { EduAdWigetsByPosition, EduRankingList, EduRankingListItem } from 'interfaces/EduRanking';
import ProductCard from 'components/ProductCard';
import ProgramsRankingsFilters, {
  FilterFields,
  FilterKeys,
  type ProgramsFilterOptions,
  type ProgramsFilters,
  SortingOptions,
} from 'components/education/EduRanking/ProgramsFilters';
import type { ChipFilter } from 'components/Globals/FilterChips';
import type { FilterRange } from 'api/fragments';
import nFormatter from 'utils/numberUtils';
import { ParseHtmlDefault } from 'utils/miscUtils/rawHtml';
import SearchWButton from 'components/Globals/SearchWButton';
import JumpTo from 'components/JumpTo/JumpTo';
import ScrollUpButton from 'components/Globals/Base/ScrollUpButton';
import type { Option } from 'components/Globals/Base/Dropdown';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import EDUCATION_ADS_TEST_IDS from 'constants/testsIds/educationAds';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import dynamic from 'next/dynamic';
import EDUCATION_RANKINGS_TEST_IDS from 'constants/testsIds/EducationRankings';
import * as S from './RankingPageStyles';

export interface EduRankingPageProps {
  rankingList: EduRankingList;
  adWidgets: EduAdWigetsByPosition;
}

const FilterChips = dynamic(() => import('components/Globals/FilterChips'));
const PaidPlacement = dynamic(() => import('components/PaidPlacement'));
const EduRelated = dynamic(() => import('components/education/EduRelated'));

const AD_INTERVAL = 5;
const AD_OFFSET = 3;

const EduRankingPage: React.FC<EduRankingPageProps> = ({ rankingList, adWidgets }) => {
  const { title, intro, description, programs, breadcrumbs, hideRanking, relatedArticles, relatedRankings } =
    rankingList;
  const { headerHeight } = useHeaderHeight();

  const getNewRangeValues = (value: number, currentRangeValue?: FilterRange): FilterRange => {
    if (currentRangeValue?.min === undefined || currentRangeValue?.max === undefined) return { max: value, min: value };

    const minValue = value < currentRangeValue.min ? value : currentRangeValue.min;
    const maxValue = value > currentRangeValue.max ? value : currentRangeValue.max;
    return {
      max: maxValue,
      min: minValue,
    };
  };

  const priceLabel = useMemo(
    () =>
      programs[0]?.properties.find((property) => property.name.includes(FilterFields[FilterKeys.price]))?.name ||
      FilterFields[FilterKeys.price],
    [programs],
  );

  // Transforms price to an int number ex. '$85,854.00' -> 85854
  // Formats not supported: '$52,069–$69,425', '$2169 per credit', '$767 per credit hour'
  const formatPrice = (price: string) => {
    const formattedPriceValue = price.replaceAll('$', '').replaceAll(',', ''); // $61,056 -> 61056
    return Math.round(Number(formattedPriceValue));
  };

  const filterOptions = useMemo(() => {
    const sortingOptions = {
      [FilterKeys.sortBy]: Object.values(SortingOptions),
    };
    const programsFilterOptions = { ...sortingOptions } as ProgramsFilterOptions;
    const cities: Option[] = [];
    const states: Option[] = [];
    programs.forEach(({ locationData, properties }) => {
      cities.push({ label: `${locationData.city}, ${locationData.state}`, value: locationData.city });
      states.push({
        label: locationData.stateName,
        value: `${locationData.stateName} ${locationData.state}`,
      });

      const currentGMATRequirementOptions = new Set(programsFilterOptions[FilterKeys.gmatRequirement]);
      const currentCreditsRange = programsFilterOptions[FilterKeys.creditsRange];
      const currentPriceRange = programsFilterOptions[FilterKeys.price];

      properties.forEach((property) => {
        if (property.name.toLowerCase().includes(FilterFields[FilterKeys.price].toLowerCase())) {
          const price = formatPrice(property.value);
          if (!Number.isNaN(price)) {
            const newPrice = getNewRangeValues(price, currentPriceRange);
            Object.assign(programsFilterOptions, {
              [FilterKeys.price]: newPrice,
            });
          }
        } else {
          switch (property.name) {
            case FilterFields[FilterKeys.creditsRange]: {
              const creditsNr = Number(property.value);
              if (!Number.isNaN(creditsNr)) {
                Object.assign(programsFilterOptions, {
                  [FilterKeys.creditsRange]: getNewRangeValues(creditsNr, currentCreditsRange),
                });
              }
              break;
            }
            case FilterFields[FilterKeys.gmatRequirement]:
              currentGMATRequirementOptions.add(property.value);
              Object.assign(programsFilterOptions, {
                [FilterKeys.gmatRequirement]: Array.from(currentGMATRequirementOptions),
              });
              break;
            default:
              break;
          }
        }
      });
    });

    const citiesSet = new Set(cities.map((city) => JSON.stringify(city)));
    const statesSet = new Set(states.map((state) => JSON.stringify(state)));
    const sortedCitiesArray = Array.from(citiesSet)
      .map((city) => JSON.parse(city) as Option)
      .sort((el1, el2) => el1.label?.localeCompare(el2.label));
    const sortedStatesArray = Array.from(statesSet)
      .map((state) => JSON.parse(state) as Option)
      .sort((el1, el2) => el1.label?.localeCompare(el2.label));
    Object.assign(programsFilterOptions, {
      [FilterKeys.locations]: sortedStatesArray.concat(sortedCitiesArray),
    });

    return programsFilterOptions;
  }, [programs]);

  const initiallyAppliedFilters = useMemo(() => {
    const credits = filterOptions[FilterKeys.creditsRange];
    const price = filterOptions[FilterKeys.price];
    const gmatRequirement = filterOptions[FilterKeys.gmatRequirement];

    return {
      [FilterKeys.searchQuery]: '',
      ...(credits && {
        [FilterKeys.creditsRange]: {
          max: credits.max,
          min: credits.min,
        },
      }),
      ...(price && {
        [FilterKeys.price]: {
          max: price.max,
          min: price.min,
        },
      }),
      ...(gmatRequirement && {
        [FilterKeys.gmatRequirement]: '',
      }),
      [FilterKeys.locations]: '',
      [FilterKeys.sortBy]: SortingOptions.RANK,
    };
  }, [filterOptions]);

  const [appliedFilters, setAppliedFilters] = useState<ProgramsFilters>(initiallyAppliedFilters);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [chipFilters, setChipFilters] = useState<ChipFilter[]>([]);
  const [programsRankingList, setProgramsRankingList] = useState(programs);
  const [shouldUpdateFilters, setShouldUpdateFilters] = useState(false);

  const sortByRank = (el1: EduRankingListItem, el2: EduRankingListItem) => el1.rank - el2.rank;

  const sortProgramsBy = useCallback(
    (sortingOption: SortingOptions, list: EduRankingListItem[]) =>
      list.sort((el1, el2) =>
        sortingOption === SortingOptions.RANK ? sortByRank(el1, el2) : el1.name.localeCompare(el2.name),
      ),
    [],
  );

  const getProgramPropriety = (program: EduRankingListItem, searchedProprietyName: string) =>
    program.properties.find((propriety) => propriety.name === searchedProprietyName);

  const filterPrograms = useCallback(
    (programsList: EduRankingListItem[], filters: ProgramsFilters) => {
      const filtersEntries = Object.entries(filters) as Array<
        [keyof ProgramsFilters, ProgramsFilters[keyof ProgramsFilters]]
      >;
      return programsList.filter((program) =>
        filtersEntries.every(([filterKey, filterValue]) => {
          if (!filterValue) return true;
          // some programs that have properties with unsupported formats are excluded from the filtering
          // when a filter chip is reset we need to make sure the excluded programs are also shown
          if (typeof filterValue === 'string' && initiallyAppliedFilters[filterKey] === filterValue) return true;
          if (
            typeof filterValue !== 'string' &&
            (initiallyAppliedFilters[filterKey] as FilterRange).min === filterValue.min &&
            (initiallyAppliedFilters[filterKey] as FilterRange).max === filterValue.max
          )
            return true;

          switch (filterKey) {
            case FilterKeys.locations: {
              const { city, stateName } = program.locationData;
              return (
                city?.toUpperCase() === filterValue.toString().toUpperCase() ||
                filterValue.toString().toUpperCase().includes(stateName?.toUpperCase())
              );
            }
            case FilterKeys.searchQuery:
              return program.name.toUpperCase().includes(filterValue.toString().toUpperCase());
            case FilterKeys.creditsRange: {
              const filterValueRange = filterValue as FilterRange;
              const credits = getProgramPropriety(program, FilterFields[FilterKeys.creditsRange])?.value;
              return credits !== undefined
                ? Number(credits) >= filterValueRange.min && Number(credits) <= filterValueRange.max
                : false;
            }
            case FilterKeys.price: {
              const filterValueRange = filterValue as FilterRange;
              const price = getProgramPropriety(program, priceLabel)?.value;
              if (price !== undefined) {
                const formattedPrice = formatPrice(price);
                if (!Number.isNaN(formattedPrice)) {
                  return formattedPrice >= filterValueRange.min && formattedPrice <= filterValueRange.max;
                }
                return false;
              }
              return false;
            }
            case FilterKeys.gmatRequirement: {
              const gmatRequirement = getProgramPropriety(program, FilterFields[FilterKeys.gmatRequirement])?.value;
              return filterValue === gmatRequirement;
            }
            default:
              return true;
          }
        }),
      );
    },
    [initiallyAppliedFilters, priceLabel],
  );

  const buildChips = useCallback(
    (filters: ProgramsFilters, chipsSuffix?: Record<Partial<keyof ProgramsFilters>, string>) => {
      const filtersEntries = Object.entries(filters) as [
        keyof ProgramsFilters,
        ProgramsFilters[keyof ProgramsFilters],
      ][];
      return filtersEntries.map(([filterKey, filterValue]) => {
        const initialKeyValue = initiallyAppliedFilters[filterKey];
        if (JSON.stringify(initialKeyValue) === JSON.stringify(filterValue)) return {};

        if (typeof filterValue === 'object') {
          const label = Object.values(filterValue)
            .sort((firstVal, secondVal) => firstVal - secondVal)
            .map((val) => nFormatter(val as number))
            .join(' - ');

          return {
            keyName: filterKey,
            title: FilterFields[filterKey],
            value: chipsSuffix ? label + chipsSuffix[filterKey] : label,
          };
        }

        return {
          keyName: filterKey,
          title: FilterFields[filterKey],
          value: filterValue,
        };
      }) as ChipFilter[];
    },
    [initiallyAppliedFilters],
  );

  const handleApplyFilters = useCallback(
    (filters: ProgramsFilters, updateChips = true, resetQuery = false) => {
      // Use searchQuery state when filter doesn't have query (aka when clicking on "apply filters")
      let query = filters[FilterKeys.searchQuery] ? filters[FilterKeys.searchQuery] : searchQuery;
      // Clear query state when removing chips;
      query = resetQuery ? '' : query;
      setSearchQuery(query);
      const updatedFilters = { ...filters, [FilterKeys.searchQuery]: query };
      setAppliedFilters(updatedFilters);
      const filteredPrograms = filterPrograms(programs, updatedFilters);
      const sortedPrograms = sortProgramsBy(updatedFilters[FilterKeys.sortBy], filteredPrograms);
      setProgramsRankingList(sortedPrograms);

      if (updateChips) {
        const chips = buildChips(updatedFilters);
        setChipFilters(chips);
      }
    },
    [buildChips, filterPrograms, programs, sortProgramsBy, searchQuery],
  );

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setAppliedFilters(initiallyAppliedFilters);
    const sortedPrograms = sortProgramsBy(initiallyAppliedFilters[FilterKeys.sortBy], programs);
    setProgramsRankingList(sortedPrograms);
    setChipFilters([]);
  }, [initiallyAppliedFilters, programs, sortProgramsBy]);

  const handleRemoveFilterChip = (key: string) => {
    let resetQuery = false;
    if (key === FilterKeys.searchQuery) {
      setSearchQuery('');
      resetQuery = true;
    }
    const updatedFilters = { ...appliedFilters, [key]: initiallyAppliedFilters[key as keyof ProgramsFilters] };
    setAppliedFilters(updatedFilters);
    handleApplyFilters(updatedFilters, false, resetQuery);
    setShouldUpdateFilters(true);
  };

  const handleSearchQuery = (value: string) => {
    setSearchQuery(value);
    const updateFilters = { ...appliedFilters, [FilterKeys.searchQuery]: value };
    handleApplyFilters(updateFilters, true);
  };

  const shouldInsertAd = (index: number) => index % AD_INTERVAL === AD_OFFSET - 1;
  const getAdIndex = (index: number) => Math.floor(index / AD_INTERVAL);

  const contents = programsRankingList.map((program) => ({
    name: program.name,
    rank: program.rank,
  }));

  return (
    <S.Wrapper>
      <S.Header>
        <PageHeader
          className='page-header'
          title={title}
          description={intro}
          breadcrumbs={breadcrumbs}
        />
      </S.Header>
      <S.FilterContentWrapper>
        <ErrorBoundary fragment={UIFragments.SEARCH_W_BUTTON}>
          <S.Search>
            <SearchWButton
              initialValue={searchQuery}
              placeholder='Search schools'
              size='medium'
              $variant='discrete'
              onChange={(value) => handleSearchQuery(value)}
            />
          </S.Search>
        </ErrorBoundary>
        <ProgramsRankingsFilters
          defaultFilters={initiallyAppliedFilters}
          priceLabel={priceLabel}
          filterOptions={filterOptions}
          onApplyFiltersClick={handleApplyFilters}
          onResetFiltersClick={handleResetFilters}
          shouldUpdateFilters={shouldUpdateFilters}
          setShouldUpdateFilters={setShouldUpdateFilters}
          appliedFilters={appliedFilters}
          resultsCount={programsRankingList.length}
        />
      </S.FilterContentWrapper>
      <JumpTo
        contents={contents}
        headerHeight={headerHeight}
        className='jump-to'
      />
      <S.Content $headerHeight={headerHeight}>
        {chipFilters.length > 0 && (
          <FilterChips
            chipFilters={chipFilters}
            setChipFilters={setChipFilters}
            handleRemoveFilterChip={handleRemoveFilterChip}
            showChips
          />
        )}
        <S.RankingList data-cy={EDUCATION_RANKINGS_TEST_IDS.RANKING_LIST}>
          {programsRankingList.length <= 0 && (
            <S.ResultText className='bold'>
              No results found matching your search criteria. Please search again.
            </S.ResultText>
          )}
          {adWidgets?.header?.map((headerAd, index) => (
            <PaidPlacement
              dataCy={`${EDUCATION_ADS_TEST_IDS.RANKINGS_PAGE_HEADER}${index}`}
              key={`pp-${headerAd.order}`}
              adContent={headerAd.content}
              className={'paid-placement-item'}
              adTag={headerAd.adTag}
              position='header'
              utmCampaign='ranking'
              entityId={rankingList.prefixedDatabaseId}
            />
          ))}

          {programsRankingList.map((program: EduRankingListItem, index) => (
            <React.Fragment key={program.databaseId}>
              <ProductCard
                dataCy={EDUCATION_RANKINGS_TEST_IDS.PRODUCT_CARD + index.toString()}
                key={program.databaseId}
                className='ranking-card'
                productCardData={{
                  bottomSection: {
                    tables: [
                      {
                        values: program.properties
                          .filter((property) => property.visible)
                          .map((property) => ({
                            title: property.name,
                            value: property.value,
                          }))
                          .sort((property1, property2) => property1.title.localeCompare(property2.title)),
                      },
                    ],
                  },
                  description: {
                    text: program.description,
                  },
                  id: `${program.rank}`,
                  image: {
                    alt: program.image.altText,
                    caption: program.image.caption,
                    src: program.image.src ? program.image.src : '',
                  },
                  isAdvertisement: { text: '', value: false },
                  sections: [],
                  subtitle: program.location,
                  title: `${!hideRanking ? `${program.rank}. ` : ''}${program.name}`,
                  titleLink: program.itemLink,
                }}
              />
              {shouldInsertAd(index) && adWidgets && adWidgets.in_stream && adWidgets.in_stream[getAdIndex(index)] && (
                <PaidPlacement
                  dataCy={`${EDUCATION_ADS_TEST_IDS.RANKINGS_PAGE_PROGRAMS}${index}`}
                  key={`pp-${adWidgets.in_stream[getAdIndex(index)].order}`}
                  adContent={adWidgets.in_stream[getAdIndex(index)].content}
                  adTag={adWidgets.in_stream[getAdIndex(index)].adTag}
                  position='in_stream'
                  utmCampaign='ranking'
                  entityId={rankingList.prefixedDatabaseId}
                />
              )}
            </React.Fragment>
          ))}

          {adWidgets?.footer?.map((footerAd, index) => (
            <PaidPlacement
              dataCy={`${EDUCATION_ADS_TEST_IDS.RANKINGS_PAGE_FOOTER}${index}`}
              key={`pp-${footerAd.order}`}
              adContent={footerAd.content}
              adTag={footerAd.adTag}
              position='footer'
              utmCampaign='ranking'
              entityId={rankingList.prefixedDatabaseId}
            />
          ))}
        </S.RankingList>
        <S.ContentDescription>{ParseHtmlDefault(description)}</S.ContentDescription>
        {((relatedArticles && relatedArticles.length > 0) || (relatedRankings && relatedRankings.length > 0)) && (
          <ErrorBoundary fragment={UIFragments.EDU_RELATED_ARTICLES}>
            <EduRelated
              articles={relatedArticles}
              rankings={relatedRankings}
              title='Related Articles'
            />
          </ErrorBoundary>
        )}
        <ScrollUpButton
          dataCy={EDUCATION_RANKINGS_TEST_IDS.SCROLL_UP_BUTTON}
          isRecommends
        />
      </S.Content>
    </S.Wrapper>
  );
};

export default EduRankingPage;
